import React, { useCallback, useEffect, useState } from 'react';
import {
  clearFindRecordRequest,
  fetchConversationIngestPlanes,
  queryGroupEntries,
  setSessionLockedRecord,
} from '../utils/api';
import './ConversationIngestModal.css';

function rowLabelFromRaw(raw = {}) {
  const candidates = ['name', 'title', 'company', 'company_name', 'label', 'doc_slug', 'id'];
  for (const k of candidates) {
    const v = raw?.[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  const first = Object.values(raw || {}).find((v) => typeof v === 'string' && v.trim());
  return first ? String(first).slice(0, 80) : 'Row';
}

function formatPreview(raw = {}) {
  const entries = Object.entries(raw || {})
    .filter(([k, v]) => v != null && String(v).trim() !== '' && !String(k).startsWith('_'))
    .slice(0, 4);
  if (!entries.length) return '(empty)';
  return entries.map(([k, v]) => `${k}: ${String(v).slice(0, 60)}`).join(' · ');
}

/**
 * In-chat Find Record flow: pick ingestion source → search → confirm lock.
 */
export default function FindRecordModal({
  open,
  onClose,
  suffix,
  projectId,
  sessionId,
  initialGroupSlug = null,
  onLocked = null,
}) {
  const [step, setStep] = useState('source'); // source | search | confirm
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [planes, setPlanes] = useState([]);
  const [groupSlug, setGroupSlug] = useState(initialGroupSlug || '');
  const [searchText, setSearchText] = useState('');
  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const reset = useCallback(() => {
    setStep(initialGroupSlug ? 'search' : 'source');
    setError('');
    setPlanes([]);
    setGroupSlug(initialGroupSlug || '');
    setSearchText('');
    setEntries([]);
    setSelectedEntry(null);
  }, [initialGroupSlug]);

  useEffect(() => {
    if (!open) {
      reset();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        if (sessionId) {
          try { await clearFindRecordRequest(sessionId); } catch (_) { /* ignore */ }
        }
        const data = await fetchConversationIngestPlanes({ suffix, projectId });
        if (cancelled) return;
        const list = data.planes || [];
        setPlanes(list);
        const startSlug = initialGroupSlug || (list.length === 1 ? list[0].group_slug : '');
        setGroupSlug(startSlug || '');
        setStep(startSlug ? 'search' : 'source');
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load ingestion sources');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, suffix, projectId, sessionId, initialGroupSlug, reset]);

  const runSearch = async (q = searchText) => {
    if (!suffix || !groupSlug) {
      setError('Select an ingestion source first');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const trimmed = String(q || '').trim().toLowerCase();
      const data = await queryGroupEntries({
        suffix,
        groupSlug,
        filters: {},
        limit: 100,
        offset: 0,
      });
      let list = data.entries || [];
      if (trimmed) {
        list = list.filter((entry) => {
          const hay = JSON.stringify(entry?.raw || {}).toLowerCase();
          const id = String(entry?.id || entry?.entry_id || '').toLowerCase();
          return hay.includes(trimmed) || id.includes(trimmed);
        });
      }
      setEntries(list.slice(0, 40));
      setStep('search');
    } catch (err) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || step !== 'search' || !groupSlug || !suffix) return undefined;
    runSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, groupSlug, suffix]);

  const handleSelectEntry = (entry) => {
    setSelectedEntry(entry);
    setStep('confirm');
  };

  const handleConfirmLock = async () => {
    if (!selectedEntry || !sessionId || !groupSlug) return;
    setLoading(true);
    setError('');
    try {
      const entryId = selectedEntry.id || selectedEntry.entry_id;
      const label = rowLabelFromRaw(selectedEntry.raw || {});
      const data = await setSessionLockedRecord({
        sessionId,
        group_slug: groupSlug,
        entry_id: entryId,
        label,
      });
      onLocked?.(data.locked_record || {
        group_slug: groupSlug,
        entry_id: entryId,
        label,
      });
      onClose?.({ locked: true });
    } catch (err) {
      setError(err.message || 'Failed to lock record');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const selectedPlane = planes.find((p) => p.group_slug === groupSlug);

  return (
    <div className="conversation-ingest-overlay" role="dialog" aria-modal="true" aria-labelledby="find-record-title">
      <div className="conversation-ingest-modal" style={{ width: 'min(720px, 96vw)', minHeight: 'min(50vh, 420px)' }}>
        <div className="conversation-ingest-header">
          <h3 id="find-record-title">Find Record</h3>
          <p className="conversation-ingest-hint" style={{ margin: 0 }}>
            Search an ingestion source and lock a row to this conversation.
          </p>
        </div>

        <div className="conversation-ingest-body">
          {error && (
            <div className="conversation-ingest-error" style={{ color: '#b91c1c', marginBottom: 12 }}>
              <i className="fas fa-triangle-exclamation" /> {error}
            </div>
          )}

          {step === 'source' && (
            <>
              <p><strong>Which ingestion source?</strong></p>
              <div className="conversation-ingest-plane-list">
                {planes.map((plane) => (
                  <label
                    key={plane.group_slug}
                    className={`conversation-ingest-plane-item${groupSlug === plane.group_slug ? ' selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="find-record-source"
                      checked={groupSlug === plane.group_slug}
                      onChange={() => setGroupSlug(plane.group_slug)}
                    />
                    <span>
                      <strong>{plane.name || plane.group_slug}</strong>
                      <div className="conversation-ingest-hint">{plane.group_slug}</div>
                    </span>
                  </label>
                ))}
                {!loading && planes.length === 0 && (
                  <p className="conversation-ingest-hint">No ingestion sources available for this project.</p>
                )}
              </div>
            </>
          )}

          {step === 'search' && (
            <>
              <p>
                <strong>Source:</strong> {selectedPlane?.name || groupSlug}
                {!initialGroupSlug && (
                  <button
                    type="button"
                    className="btn ghost"
                    style={{ marginLeft: 8, fontSize: 12, padding: '2px 8px' }}
                    onClick={() => { setStep('source'); setEntries([]); setSelectedEntry(null); }}
                    disabled={loading}
                  >
                    Change
                  </button>
                )}
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input
                  type="search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                  placeholder="Search records…"
                  style={{ flex: 1, padding: '8px 10px' }}
                  disabled={loading}
                />
                <button type="button" className="btn" onClick={() => runSearch()} disabled={loading || !groupSlug}>
                  <i className="fas fa-search" /> Search
                </button>
              </div>
              <div className="conversation-ingest-plane-list">
                {(entries || []).map((entry) => {
                  const id = entry.id || entry.entry_id;
                  const label = rowLabelFromRaw(entry.raw || {});
                  return (
                    <button
                      type="button"
                      key={id}
                      className="conversation-ingest-plane-item"
                      style={{ textAlign: 'left', width: '100%' }}
                      onClick={() => handleSelectEntry(entry)}
                      disabled={loading}
                    >
                      <span>
                        <strong>{label}</strong>
                        <div className="conversation-ingest-hint">{formatPreview(entry.raw)}</div>
                        <div className="conversation-ingest-hint" style={{ opacity: 0.7 }}>{id}</div>
                      </span>
                    </button>
                  );
                })}
                {!loading && entries.length === 0 && (
                  <p className="conversation-ingest-hint">No matching records.</p>
                )}
              </div>
            </>
          )}

          {step === 'confirm' && selectedEntry && (
            <div className="conversation-ingest-no-row">
              <p className="conversation-ingest-no-row-prompt">
                Do you want us to lock this record to the conversation?
              </p>
              <div className="conversation-ingest-row-card" style={{ marginTop: 12 }}>
                <div className="conversation-ingest-row-title">
                  <span>{rowLabelFromRaw(selectedEntry.raw || {})}</span>
                </div>
                <p className="conversation-ingest-hint">{formatPreview(selectedEntry.raw)}</p>
                <p className="conversation-ingest-hint">
                  {selectedPlane?.name || groupSlug} · {selectedEntry.id || selectedEntry.entry_id}
                </p>
              </div>
              <p className="conversation-ingest-hint" style={{ marginTop: 12 }}>
                Future prompts will focus edits and updates on this record until you unlock it.
              </p>
            </div>
          )}

          {loading && (
            <p className="conversation-ingest-hint" style={{ marginTop: 12 }}>
              <i className="fas fa-spinner fa-spin" /> Working…
            </p>
          )}
        </div>

        <div className="conversation-ingest-footer">
          <button type="button" className="btn ghost" onClick={() => onClose?.({ locked: false })} disabled={loading}>
            Cancel
          </button>
          {step === 'source' && (
            <button
              type="button"
              className="btn"
              disabled={loading || !groupSlug}
              onClick={() => setStep('search')}
            >
              Continue
            </button>
          )}
          {step === 'confirm' && (
            <>
              <button
                type="button"
                className="btn ghost"
                disabled={loading}
                onClick={() => { setStep('search'); setSelectedEntry(null); }}
              >
                Back
              </button>
              <button type="button" className="btn" disabled={loading} onClick={handleConfirmLock}>
                <i className="fas fa-lock" /> Yes, lock record
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
